import { PositionalAudio, Text, useMatcapTexture, shaderMaterial, Sparkles, Center, useTexture, useGLTF, OrbitControls, Text3D, Float } from '@react-three/drei'
import * as THREE from 'three'
import { useFrame, extend } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import portalVertexShader from './shaders/portal/vertex.glsl'
import portalFragmentShader from './shaders/portal/fragment.glsl'
import Frog from './Frog'
import Hearts from './Hearts'

const material = new THREE.MeshMatcapMaterial()
const PortalMaterial = shaderMaterial(
  {
    uTime: 0,
    uColorStart: new THREE.Color('#BC7CD6'),
    uColorEnd: new THREE.Color('#000000')
  },
  portalVertexShader,
  portalFragmentShader
)

extend({ PortalMaterial })

export default function Experience() {
  const [matcapTexture] = useMatcapTexture('7B5254_E9DCC7_B19986_C8AC91', 256)
  const { nodes } = useGLTF('./model/portal.glb')

  const bakedTexture = useTexture('./model/baked.jpg')
  bakedTexture.flipY = false

  const portalMaterial = useRef()

  // Shared parent for frog + heart
  const buddyRef = useRef()
  // base position for the buddy group (so we can animate relative to it)
  const basePos = useRef(new THREE.Vector3(0.6, 0.1, -0.1))

  useFrame((state, delta) => {
    // portal shader time
    portalMaterial.current.uTime += delta

    // animate the shared group so they float together
    const t = state.clock.getElapsedTime()
    if (buddyRef.current) {
      buddyRef.current.position.set(
        basePos.current.x,
        basePos.current.y + Math.sin(t * 2) * 0.1 + 0.1, // bob together
        basePos.current.z
      )
      buddyRef.current.rotation.y = Math.sin(t * 1.0) * 0.2 // gentle shared sway
    }
  })

  useEffect(() => {
    matcapTexture.colorSpace = THREE.SRGBColorSpace
    matcapTexture.needsUpdate = true
    material.matcap = matcapTexture
    material.needsUpdate = true
  }, [matcapTexture])

  return (
    <>
      <OrbitControls makeDefault />

      {/* Center only the baked portal + static meshes */}
      <Center>
        <mesh geometry={nodes.baked.geometry}>
          <meshBasicMaterial map={bakedTexture} />
        </mesh>

        <mesh geometry={nodes.poleLightA.geometry} position={nodes.poleLightA.position}>
          <meshBasicMaterial color="#ffffe5" />
        </mesh>

        <mesh geometry={nodes.poleLightB.geometry} position={nodes.poleLightB.position}>
          <meshBasicMaterial color="#ffffe5" />
        </mesh>

        <mesh
          geometry={nodes.portalLight.geometry}
          position={nodes.portalLight.position}
          rotation={nodes.portalLight.rotation}
        >
          <portalMaterial ref={portalMaterial} />
        </mesh>
      </Center>

      {/* Floating text */}
      <Float>
        <Text3D
          material={material}
          font="./fonts/helvetiker_regular.typeface.json"
          size={0.5}
          height={0.2}
          curveSegments={12}
          bevelEnabled
          bevelThickness={0.02}
          bevelSize={0.02}
          bevelOffset={0}
          bevelSegments={5}
          position={[-2, 2, 0]}
        >
          THANK YOU!
        </Text3D>

        {/* Manual spaced variant kept as-is */}
        <Text3D
          material={material}
          font="./fonts/helvetiker_regular.typeface.json"
          size={0.1}
          height={0.2}
          curveSegments={12}
          bevelEnabled
          bevelThickness={0.02}
          bevelSize={0.02}
          bevelOffset={0}
          bevelSegments={5}
          position={[-1.64, 1.6, 0]}
        >
          F O R  Y O U R  C O N S I D E R A T I O N ,   R I B B I T !
        </Text3D>
      </Float>

      {/* Stars */}
      <Sparkles size={6} scale={[4, 2, 4]} position-y={1} speed={0.2} count={40} />

      {/* Light near the portal to give the frog some pop */}
      <pointLight position={[0, 0, 0]} intensity={4} distance={5} color={'#ffffff'} />

      {/* Shared group: moves BOTH frog and heart together */}
      <group ref={buddyRef} position={basePos.current} >
        <PositionalAudio
          url="./sounds/frog-sound.mp3"
          loop
          distance={5}
          ref={(audio) => {
            if (audio) {
              audio.setVolume(1.3)
              // Play audio on first user interaction
              const playAudio = () => {
                if (audio.context.state === 'suspended') {
                  audio.context.resume()
                }
                audio.play()
                window.removeEventListener('click', playAudio)
                window.removeEventListener('keydown', playAudio)
              }
              window.addEventListener('click', playAudio)
              window.addEventListener('keydown', playAudio)
            }
          }}
        />
        <Frog scale={0.7} position={[-0.05, 0.15, -0.01]} rotation={[0, -Math.PI/2, 0]} />
        {/* Heart inherits the group's bob/sway; keeps its own spin */}
        <Hearts position={[-0.7, -0.19, -1]} scale={0.12} />
      </group>
    </>
  )
}
